import { useEffect, useState } from 'preact/hooks'
import {
  MESH_MAX, STATUS_OPTIONEN, statusFrisch, statusOption, type Presence,
} from '@fh/shared'
import type { UebersetzungAusgang } from '@fh/shared/sprache'

import { anrufUebergeben } from '../anruf/klingeln.js'
import { api, ApiError, type Me } from '../api.js'
import { Avatar } from '../Avatar.js'
import { PersonMenue } from '../PersonMenue.js'
import { t } from '../i18n/t.js'
import { ZweisprachigInline } from '../i18n/Zweisprachig.js'
import { anwesenheit, listenZeit, wann } from '../i18n/zeit.js'
import { bekannteVorschau, vorschauenLaden, type LetzteNachricht } from '../vorschau.js'
import { onEvent } from '../ws.js'

/**
 * Familien-Startseite (Anf. 28, 54, 77; Designregel Anf. 78).
 *
 * Der Bildschirm, an dem sich entscheidet, ob die App bedienbar ist. Drei
 * Regeln, die hier durchgehalten werden:
 *
 *  1. **Eine Person, eine Karte, die Aktionen direkt darauf.** Nicht: Karte
 *     antippen, Profil öffnen, dort ein Menü suchen. Wer Oma anrufen will,
 *     tippt auf „Anrufen" unter Omas Namen (Szenarien 1, 2 und 6 sind damit
 *     zwei Tipps).
 *  2. **Wer Aufmerksamkeit braucht, steht oben.** Die Sortierung kommt vom
 *     Server und richtet sich nach dem Status, nicht nach dem Alphabet.
 *  3. **Keine Zahl ohne Wort.** „3" neben einem Symbol ist eine Rätselaufgabe;
 *     „3 neue Nachrichten" ist eine Information.
 */

interface Mitglied {
  id: string
  displayName: string
  role: string
  presence: Presence
  lastSeenAt: string | null
  statusKey: string | null
  statusText: string | null
  statusAt: string | null
  directRoomId: string | null
  unread: number
  /** Die letzte Nachricht des Direktchats — für die Vorschauzeile. */
  lastMessage: LetzteNachricht | null
  lastMessageAt: string | null
  uebersetzung?: UebersetzungAusgang
}

interface DashboardDaten {
  me: {
    id: string; displayName: string
    statusKey: string | null; statusText: string | null; statusAt: string | null
    uebersetzung?: UebersetzungAusgang
  }
  members: Mitglied[]
  unreadTotal: number
  missedCalls: number
  activeCall: { id: string; roomId: string; kind: string } | null
  /**
   * Von wem die verpassten Anrufe waren — höchstens drei, neueste zuerst.
   *
   * Optional im Typ, obwohl der Server es immer liefert: Bei einem rollenden
   * Update spricht ein frisch geladener Client kurz mit einem alten Server.
   */
  missedFrom?: Array<{ personId: string; at: string }>
  /**
   * Der Familienchat als eigene Zeile.
   *
   * Optional im Typ, obwohl der Server ihn immer liefert — dieselbe Vorsicht
   * wie beim Verlauf darunter: Bei einem rollenden Update spricht ein frisch
   * geladener Client kurz mit einem alten Server.
   */
  familyRoom?: {
    id: string
    title: string | null
    unread: number
    lastMessage: LetzteNachricht | null
    lastMessageAt: string | null
  } | null
  /**
   * Optional im Typ, obwohl der Server es immer liefert: Bei einem rollenden
   * Update spricht ein frisch geladener Client kurz mit einem alten Server.
   * Eine Startseite, die daran zerbricht, ist schlimmer als eine ohne Verlauf.
   */
  recentStatus?: Array<{
    id: string; personId: string; statusKey: string | null
    statusText: string | null; createdAt: string
    uebersetzung?: UebersetzungAusgang
  }>
}

export interface StartAktionen {
  /** Chat mit dieser Person öffnen — legt den Raum an, falls es keinen gibt. */
  onChat: (personId: string, roomId: string | null) => void
  onAnruf: (personId: string, roomId: string | null, kind: 'audio' | 'video') => void
  onFoto: (personId: string, roomId: string | null) => void
}

export function Start({ me, aktionen }: { me: Me; aktionen: StartAktionen }) {
  const [daten, setDaten] = useState<DashboardDaten | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  /** Die Person, deren Menü gerade offen ist. */
  const [menue, setMenue] = useState<Mitglied | null>(null)
  /*
   * Der Anstoß, sich neu zu zeichnen, wenn Vorschauen entschlüsselt sind.
   *
   * Nur der Setter wird gebraucht: Der Zwischenspeicher liegt im Modul
   * `vorschau.ts` und ändert sich, ohne dass Preact davon erfährt — dieselbe
   * Lage wie bei den Profilbildern. Der Zähler selbst hat keine Bedeutung; er
   * ist nur das, was sich ändert. Ohne ihn blieben die Zeilen bei ihrem
   * Ersatztext stehen, obwohl der Text längst da ist.
   */
  const [, setVorschauStand] = useState(0)

  async function laden() {
    try {
      // EIN Aufruf für die ganze Seite. Begründung in
      // modules/dashboard/service.ts — auf 3G zählt jede Rückfrage.
      const frisch = await api.get<DashboardDaten>('/dashboard')
      setDaten(frisch)
      setFehler(null)

      /*
       * Die Vorschauen erst DANACH, und ohne darauf zu warten.
       *
       * Sie brauchen die nachgeladene Krypto und je Raum eine Entschlüsselung.
       * Stünde das vor `setDaten`, wäre die Startseite so lange leer wie die
       * langsamste Entschlüsselung dauert — auf einem alten Telefon spürbar.
       * So steht die Liste sofort, und die Zeilen füllen sich.
       */
      void vorschauenLaden([
        ...(frisch.familyRoom ? [frisch.familyRoom] : []),
        ...frisch.members
          .filter((m): m is Mitglied & { directRoomId: string } => m.directRoomId !== null)
          .map((m) => ({ id: m.directRoomId, lastMessage: m.lastMessage })),
      ]).then((neu) => { if (neu) setVorschauStand((n) => n + 1) })
    } catch (e) {
      setFehler(e instanceof ApiError && e.code === 'offline'
        ? t('Keine Verbindung. Du siehst den letzten Stand.')
        : t('Die Übersicht konnte nicht geladen werden.'))
    }
  }

  useEffect(() => { void laden() }, [])

  /**
   * In das laufende Gespräch gehen.
   *
   * Beitreten und den Anruf übergeben — derselbe Weg, den die Annehmen-Kachel
   * und der Anrufknopf auf dieser Seite nehmen. Ohne die Übergabe landet man
   * auf der Anrufliste und sieht dort nur, dass irgendwo ein Anruf läuft.
   */
  async function dazukommen() {
    const call = daten?.activeCall
    if (!call) return
    try {
      anrufUebergeben(await api.post<unknown>(`/calls/${call.id}/join`, {}))
    } catch {
      // Beitreten ging nicht — dann wenigstens hingehen, statt hier zu stehen.
    }
    location.hash = '#anrufen'
  }

  // Status- und Nachrichtenänderungen wirken sofort. Eine Startseite, die den
  // Stand von vor zehn Minuten zeigt, ist schlimmer als eine leere.
  useEffect(() => onEvent((e) => {
    if (['status.changed', 'message.new', 'presence.changed', 'call.ended']
      .includes(e.type)) void laden()
  }), [])

  if (!daten) {
    return (
      <div>
        {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}
        <p class="fh-muted">{t('Wird geladen…')}</p>
      </div>
    )
  }

  // Wer wie heißt — für „Mama: Sonntags gibt es Streuselkuchen" in der Zeile.
  const namen = Object.fromEntries(daten.members.map((m) => [m.id, m.displayName]))

  return (
    <div class="fh-start">
      {fehler && <p class="fh-hinweis fh-hinweis--fehler" role="alert">{fehler}</p>}

      {daten.activeCall && (
        <p class="fh-hinweis fh-hinweis--info" role="status">
          <span aria-hidden="true">📞</span>
          <span>
            {t('Es läuft gerade ein Anruf.')}{' '}
            {/*
              Ein Knopf, kein Link.

              Hier stand `<a href="#anrufen">`, und das war eine Sackgasse: Der
              Link führte auf die Anrufliste, nicht in das laufende Gespräch.
              Wer ihn tippte, sah dieselbe Liste wie vorher und kam nicht
              hinein — „es läuft ein Anruf" ohne einen Weg dorthin.
            */}
            <button class="fh-btn fh-btn--link" onClick={() => void dazukommen()}>
              {t('Dazukommen')}
            </button>
          </span>
        </p>
      )}

      {daten.missedCalls > 0 && (
        <VerpasstHinweis
          anzahl={daten.missedCalls}
          von={daten.missedFrom ?? []}
          namen={Object.fromEntries(daten.members.map((m) => [m.id, m.displayName]))}
          onErledigt={() => void laden()}
        />
      )}

      {/*
        Die Familie steht OBEN.

        Bis zum Oberflächen-Umbau begann diese Seite mit dem eigenen Status und
        dem Verlauf; die Namen der Familie fingen erst unterhalb des
        Bildschirmrands an. Im Handy-Viewport gemessen: Man musste scrollen, um
        auf einer Seite namens „Familie" die Familie zu sehen. Das ist die
        Anordnung eines Verwaltungswerkzeugs, nicht die eines Adressbuchs.

        Szenario 4 aus Anf. 77 („Mir geht es nicht gut" in höchstens zwei
        Tipps) bleibt erfüllt: Die Statuskacheln liegen weiterhin offen da,
        nur weiter unten. Scrollen ist kein Tipp.
      */}
      <h2 class="fh-h2">{t('Deine Familie')}</h2>

      {daten.members.length === 0 && (
        <p class="fh-muted">
          {t('Noch niemand da. Lade jemanden ein — unter „Mehr" → „Familie verwalten".')}
        </p>
      )}

      <ul class="fh-zeilen">
        {daten.familyRoom && (
          <li>
            {/*
              Der Familienchat oben, als eine Zeile unter den anderen. Er ist
              kein Gegenüber, deshalb führt ein Tipp direkt hinein statt in das
              Menü — bei „allen" gibt es kein „anrufen wen".
            */}
            <button
              class="fh-zeile"
              onClick={() => { location.hash = `#chat/${daten.familyRoom!.id}` }}
            >
              <Avatar name={daten.familyRoom.title ?? t('Familienchat')} symbol="👪" />
              <span class="fh-zeile__mitte">
                <span class="fh-zeile__name">
                  {daten.familyRoom.title ?? t('Familienchat')}
                </span>
                <Vorschauzeile
                  roomId={daten.familyRoom.id}
                  letzte={daten.familyRoom.lastMessage}
                  namen={namen}
                  ichId={daten.me.id}
                  mitNamen
                  ersatz={t('Alle in der Familie')}
                />
              </span>
              <Rechts
                wann={daten.familyRoom.lastMessageAt}
                unread={daten.familyRoom.unread}
              />
            </button>
          </li>
        )}

        {daten.members.map((m) => (
          <Zeile key={m.id} mitglied={m} ichId={daten.me.id} namen={namen} onOeffnen={setMenue} />
        ))}
      </ul>

      {menue && (
        <PersonMenue
          person={menue.id}
          name={menue.displayName}
          roomId={menue.directRoomId}
          aktionen={aktionen}
          onSchliessen={() => setMenue(null)}
        />
      )}

      <EigenerStatus
        aktuell={daten.me.statusKey}
        statusAt={daten.me.statusAt}
        onGesetzt={() => void laden()}
      />

      <LetzteMeldungen
        eintraege={daten.recentStatus ?? []}
        namen={Object.fromEntries(daten.members.map((m) => [m.id, m.displayName]))}
        ichId={daten.me.id}
      />

      <p class="fh-muted">{t('Angemeldet als {wer}.', { wer: me.displayName })}</p>
    </div>
  )
}

/**
 * Die letzten Statusmeldungen — der „Verlauf für die Startseite".
 *
 * Eine Liste, keine Auswertung: chronologisch, ungefiltert, ohne
 * Zusammenfassung. Jede Verdichtung („diese Woche 3× müde") wäre eine Aussage
 * über einen Menschen, die niemand getroffen hat — und laut Anf. 10 eine
 * medizinische Schlussfolgerung.
 */
function LetzteMeldungen({ eintraege, namen, ichId }: {
  eintraege: NonNullable<DashboardDaten['recentStatus']>
  namen: Record<string, string>
  ichId: string
}) {
  if (eintraege.length === 0) return null

  return (
    <section class="fh-card">
      <h2 class="fh-h2">{t('Zuletzt gesagt')}</h2>
      <ul class="fh-liste">
        {eintraege.slice(0, 5).map((e) => {
          const option = statusOption(e.statusKey)
          return (
            <li key={e.id} class="fh-verlauf">
              <span class="fh-verlauf__wer">
                {e.personId === ichId ? t('Du') : namen[e.personId] ?? t('Jemand')}
              </span>
              <span class="fh-verlauf__was">
                {option && <><span aria-hidden="true">{option.icon}</span> {t(option.label)}</>}
                {e.statusText && (
                  <>{option && ' — '}<ZweisprachigInline
                    text={e.statusText}
                    uebersetzung={e.uebersetzung?.statusText}
                  /></>
                )}
              </span>
              <span class="fh-verlauf__wann">{wann(e.createdAt)}</span>
            </li>
          )
        })}
      </ul>
      <a class="fh-btn fh-btn--link" href="#status">{t('Alle anzeigen')}</a>
    </section>
  )
}

// --- Eine Person -------------------------------------------------------------

/**
 * „Ein verpasster Anruf von Mama" — mit Namen und mit einem Weg, ihn loszuwerden.
 *
 * ## Was hier falsch war
 *
 * Der Hinweis stand ab dem ersten verpassten Anruf **für immer**: `missed` wurde
 * gesetzt und nie wieder angefasst, und einen Wegklick-Knopf gab es nicht. Dazu
 * stand kein Name darin — man wusste, dass etwas war, und musste woanders
 * nachsehen, was. Dan hat beides gemeldet.
 *
 * ## Warum zwei Wege hinaus und nicht einer
 *
 * „Ansehen" führt zur Anrufliste; dort steht, wer wann angerufen hat, und der
 * Hinweis hat seinen Zweck erfüllt. Das ✕ ist für den anderen Fall: Man weiß
 * schon, wer es war, und will den Balken nur weghaben. Beides quittiert
 * dasselbe (`POST /calls/missed/ack`) — der Anruf bleibt in der Liste als
 * „Verpasst" stehen, denn das ist er.
 */
function VerpasstHinweis({ anzahl, von, namen, onErledigt }: {
  anzahl: number
  von: Array<{ personId: string; at: string }>
  namen: Record<string, string>
  onErledigt: () => void
}) {
  const [weg, setWeg] = useState(false)
  if (weg) return null

  async function gesehen() {
    // Sofort ausblenden, dann melden. Ein Balken, der nach dem Antippen noch
    // eine Netzrunde stehen bleibt, wirkt, als hätte der Tipp nicht gezählt.
    setWeg(true)
    try {
      await api.post('/calls/missed/ack', {})
    } catch {
      // Kein Netz: Dann steht er beim nächsten Laden wieder da. Das ist
      // richtig so — verschwiegen wäre er schlimmer als lästig.
    }
    onErledigt()
  }

  const wer = [...new Set(von.map((v) => namen[v.personId]).filter(Boolean))]

  return (
    <p class="fh-hinweis fh-hinweis--warnung fh-verpasst" role="status">
      <span aria-hidden="true">📵</span>
      <span class="fh-verpasst__text">
        {wer.length === 0
          ? (anzahl === 1
            ? t('Ein verpasster Anruf')
            : t('{n} verpasste Anrufe', { n: anzahl }))
          : anzahl === 1
            ? t('Verpasster Anruf von {wer}', { wer: wer[0]! })
            : t('{n} verpasste Anrufe — {wer}', { n: anzahl, wer: wer.join(', ') })}
        {/*
          Ein Knopf, kein Link: Er geht nicht nur hin, er quittiert auch. Ein
          `<a>`, das nebenbei einen Zustand ändert, ist eine Falle für jeden,
          der ihn in einem neuen Tab öffnet.

          Der Gedankenstrich davor ist weg: Auf einem 390 px breiten Telefon
          brach die Zeile hinter ihm um, und „—" stand allein am Zeilenende wie
          ein abgeschnittener Satz.
        */}
        <button
          class="fh-btn fh-btn--link"
          onClick={() => { void gesehen(); location.hash = '#anrufen' }}
        >
          {t('ansehen')}
        </button>
      </span>
      <button
        class="fh-verpasst__weg"
        onClick={() => void gesehen()}
        aria-label={t('Hinweis ausblenden')}
      >
        <span aria-hidden="true">✕</span>
      </button>
    </p>
  )
}

/**
 * Eine Person als Zeile: Bild, Name, darunter die letzte Nachricht oder der
 * Status, rechts Uhrzeit und Zähler.
 *
 * ## Warum die vier Aktionen nicht mehr darunter stehen
 *
 * Sie standen offen da, weil das ein Tipp statt zwei war (Anf. 77). Der Preis
 * war eine Zeile von rund 300 Pixeln je Person — bei drei Leuten kein Problem,
 * bei zwölf dreieinhalb Bildschirmhöhen. Dan hat entschieden: „stell dir vor
 * es kommen noch 10 Familienmitglieder dazu, dann scrollst du dir einen Wolf."
 *
 * Ein Tipp auf die Zeile öffnet jetzt das Menü (`PersonMenue.tsx`). Aus einem
 * Tipp werden zwei; dafür sieht man alle auf einmal, statt zu suchen.
 *
 * ## Was in der Zweitzeile steht
 *
 * Die letzte Nachricht, wie man es kennt. Nur wenn es keine gibt, tritt an
 * ihre Stelle, was vorher immer dort stand: Status oder Anwesenheit. Ein
 * auffälliger Status („Mir geht es nicht gut") verdrängt die Nachricht — das
 * ist der eine Fall, in dem die Zeile nicht wie ein Messenger aussehen soll.
 */
function Zeile({ mitglied, ichId, namen, onOeffnen }: {
  mitglied: Mitglied
  ichId: string
  namen: Record<string, string>
  onOeffnen: (m: Mitglied) => void
}) {
  const status = statusOption(mitglied.statusKey)
  const frisch = statusFrisch(mitglied.statusAt)
  const auffaellig = status?.auffaellig === true && frisch

  return (
    <li class={auffaellig ? 'fh-karte--achtung' : undefined}>
      <button class="fh-zeile" onClick={() => onOeffnen(mitglied)}>
        <Avatar name={mitglied.displayName} person={mitglied.id} />

        <span class="fh-zeile__mitte">
          <span class="fh-zeile__name fh-karte__name">{mitglied.displayName}</span>

          {auffaellig && status ? (
            <span class="fh-zeile__unter fh-karte__status">
              <span aria-hidden="true">{status.icon}</span> {t(status.label)}
              {mitglied.statusText && (
                <> — <ZweisprachigInline
                  text={mitglied.statusText}
                  uebersetzung={mitglied.uebersetzung?.statusText}
                /></>
              )}
            </span>
          ) : (
            <Vorschauzeile
              roomId={mitglied.directRoomId}
              letzte={mitglied.lastMessage}
              namen={namen}
              ichId={ichId}
              ersatz={statusOderAnwesenheit(mitglied)}
            />
          )}
        </span>

        <Rechts wann={mitglied.lastMessageAt} unread={mitglied.unread} />
      </button>
    </li>
  )
}

/** Was in der Zweitzeile steht, solange es keine Nachricht gibt. */
function statusOderAnwesenheit(mitglied: Mitglied): string {
  const status = statusOption(mitglied.statusKey)
  if (status && statusFrisch(mitglied.statusAt)) {
    return `${status.icon} ${t(status.label)}`
  }
  return anwesenheit(mitglied.presence, mitglied.lastSeenAt)
}

/**
 * Die Vorschau der letzten Nachricht — oder der Ersatztext.
 *
 * Beim ersten Zeichnen steht hier der Ersatz: Die Entschlüsselung läuft
 * asynchron und braucht die nachgeladene Krypto. Sobald sie da ist, meldet
 * `useVorschauen` und die Zeile zeichnet sich neu. Ein Ladebalken für eine
 * Zeile Text wäre mehr Unruhe als Auskunft.
 */
function Vorschauzeile({ roomId, letzte, namen, ichId, mitNamen, ersatz }: {
  roomId: string | null
  letzte: LetzteNachricht | null
  namen: Record<string, string>
  ichId: string
  /** Im Familienchat steht davor, wer geschrieben hat — bei zwei Leuten nicht. */
  mitNamen?: boolean
  ersatz: string
}) {
  const vorschau = roomId ? bekannteVorschau(roomId, letzte?.id ?? null) : undefined
  if (!vorschau) return <span class="fh-zeile__unter">{ersatz}</span>

  const wer = vorschau.senderId === ichId
    ? t('Du')
    : namen[vorschau.senderId] ?? t('Jemand')

  return (
    <span class={`fh-zeile__unter${vorschau.unlesbar ? ' fh-zeile__unter--still' : ''}`}>
      {mitNamen && <span class="fh-zeile__wer">{wer}: </span>}
      {vorschau.text}
    </span>
  )
}

/** Uhrzeit und Zähler rechts — in jeder Zeile gleich. */
function Rechts({ wann: zeitpunkt, unread }: { wann: string | null; unread: number }) {
  return (
    <span class="fh-zeile__rechts">
      {zeitpunkt && <span class="fh-zeile__wann">{listenZeit(zeitpunkt)}</span>}
      {unread > 0 && (
        <span class="fh-zaehler fh-karte__ungelesen">
          {unread}
          <span class="fh-visually-hidden">
            {unread === 1 ? ` ${t('neue Nachricht')}` : ` ${t('neue Nachrichten')}`}
          </span>
        </span>
      )}
    </span>
  )
}

// --- Eigener Status ----------------------------------------------------------

function EigenerStatus({ aktuell, statusAt, onGesetzt }: {
  aktuell: string | null
  statusAt: string | null
  onGesetzt: () => void
}) {
  const [offen, setOffen] = useState(false)
  const [busy, setBusy] = useState(false)
  const gesetzt = statusOption(aktuell)
  const frisch = statusFrisch(statusAt)

  async function setzen(key: string | null) {
    setBusy(true)
    try {
      await api.put('/me/status', { statusKey: key })
      setOffen(false)
      onGesetzt()
    } finally {
      setBusy(false)
    }
  }

  return (
    <section class="fh-eigenerstatus">
      <h2 class="fh-h2">{t('Wie geht es dir?')}</h2>

      {gesetzt && frisch ? (
        <p class="fh-eigenerstatus__aktuell">
          <span aria-hidden="true">{gesetzt.icon}</span> {t(gesetzt.label)}
          {' '}
          <button
            class="fh-btn fh-btn--link"
            disabled={busy}
            onClick={() => void setzen(null)}
          >
            {t('Zurücknehmen')}
          </button>
        </p>
      ) : (
        <p class="fh-muted">{t('Sag deiner Familie kurz Bescheid — mit einem Tipp.')}</p>
      )}

      {/*
        Aufgeklappt statt hinter einem Menü: Szenario 4 verlangt „Mir geht es
        nicht gut" in höchstens zwei Tipps. Ein Aufklapper wäre schon einer
        davon — deshalb sind die Kacheln direkt da, sobald man auf der
        Startseite ist.
      */}
      <div class="fh-eigenerstatus__kacheln" hidden={offen ? false : undefined}>
        {STATUS_OPTIONEN.map((o) => (
          <button
            key={o.key}
            class={`fh-btn fh-statuskachel${o.key === aktuell ? ' fh-statuskachel--an' : ''}`}
            disabled={busy}
            aria-pressed={o.key === aktuell}
            onClick={() => void setzen(o.key)}
          >
            <span aria-hidden="true">{o.icon}</span>
            <span>{t(o.label)}</span>
          </button>
        ))}
      </div>
      {/* `offen` steuert nur die kompakte Darstellung auf sehr kleinen Geräten. */}
      <button class="fh-btn fh-btn--link fh-nur-schmal" onClick={() => setOffen(!offen)}>
        {offen ? t('Weniger') : t('Alle Möglichkeiten')}
      </button>

      {/* Eigene Worte gehen im Status-Bereich — hier wäre ein Textfeld zu viel. */}
      <a class="fh-btn fh-btn--link" href="#status">{t('Lieber in eigenen Worten?')}</a>
    </section>
  )
}

/** Die Mesh-Grenze als Hinweis, wo sie hingehört: bevor jemand darauf stößt. */
export const VIDEO_HINWEIS = `Video geht mit bis zu ${MESH_MAX} Personen.`
